import React, { useState, useEffect } from 'react';
import { api } from './lib/api';

interface Rule {
  id: string;
  pattern: string;
  compte_pcg: string;
  taux_tva: number | null;
  priority: number;
  is_active: boolean;
}

interface AutoCatRulesProps {
  entrepriseId: string;
}

const AutoCatRules: React.FC<AutoCatRulesProps> = ({ entrepriseId }) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pattern, setPattern] = useState('');
  const [comptePcg, setComptePcg] = useState('');
  const [tauxTva, setTauxTva] = useState('20');
  const [priority, setPriority] = useState('0');
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchRules = async () => {
    try {
      const data = await api.autoCatRules.list(entrepriseId);
      setRules(data.rules);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, [entrepriseId]);

  const saveRule = async () => {
    setError(null);
    const body: any = {
      pattern,
      compte_pcg: comptePcg,
      taux_tva: tauxTva ? parseFloat(tauxTva) : null,
      priority: parseInt(priority, 10),
    };

    try {
      if (editingId) {
        await api.autoCatRules.update(editingId, body);
      } else {
        await api.autoCatRules.create(body);
      }
      resetForm();
      fetchRules();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteRule = async (id: string) => {
    await api.autoCatRules.delete(id);
    fetchRules();
  };

  const editRule = (rule: Rule) => {
    setEditingId(rule.id);
    setPattern(rule.pattern);
    setComptePcg(rule.compte_pcg);
    setTauxTva(rule.taux_tva?.toString() || '');
    setPriority(rule.priority.toString());
  };

  const resetForm = () => {
    setEditingId(null);
    setPattern('');
    setComptePcg('');
    setTauxTva('20');
    setPriority('0');
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  if (loading) return <div className="text-sm text-gray-500">Chargement...</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Règles de catégorisation automatique</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{editingId ? 'Modifier la règle' : 'Nouvelle règle'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className={labelClass}>Motif (dans le libellé)</label>
            <input value={pattern} onChange={e => setPattern(e.target.value)} placeholder="ex: CARREFOUR" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Compte PCG</label>
            <input value={comptePcg} onChange={e => setComptePcg(e.target.value)} placeholder="ex: 6061" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Taux TVA (%)</label>
            <input value={tauxTva} onChange={e => setTauxTva(e.target.value)} placeholder="20" type="number" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Priorité</label>
            <input value={priority} onChange={e => setPriority(e.target.value)} placeholder="0" type="number" className={inputClass} />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={saveRule} disabled={!pattern || !comptePcg} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {editingId ? 'Mettre à jour' : 'Ajouter'}
          </button>
          {editingId && <button onClick={resetForm} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>}
        </div>
      </div>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur : {error}</div>}

      {rules.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune règle définie.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Priorité</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Motif</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte PCG</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">TVA (%)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actif</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.id} className={`hover:bg-gray-50 transition-colors ${!rule.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{rule.priority}</td>
                  <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{rule.pattern}</td>
                  <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{rule.compte_pcg}</td>
                  <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{rule.taux_tva ?? '—'}</td>
                  <td className="px-4 py-3 border-b border-gray-100">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {rule.is_active ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-b border-gray-100">
                    <div className="flex gap-2">
                      <button onClick={() => editRule(rule)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Modifier</button>
                      <button onClick={() => deleteRule(rule.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AutoCatRules;
